/**
 * Adhoc Re-KYC (AU/NZ) API contract — frontend mirror of
 * `apps/backend/src/app/adhoc-kyc/dtos/adhoc-kyc.dto.ts`. Keep both in step.
 *
 * Endpoints are injected in T12 (`baseApi.injectEndpoints`), choosing the
 * `/adhoc-kyc/t` prefix (token in `x-adhoc-kyc-token`) when the SPA holds a
 * magic-link token and `/adhoc-kyc/me` (Transmit session) otherwise. Until
 * then this file carries the types only.
 *
 * Every response on these routes is 200 with `valid: false` for bad tokens —
 * the backend never returns 401 here, because `tokenRefreshLogic.ts` logs the
 * user out on any 401.
 */

import { baseApi } from "./base";

export type AdhocKycStatus =
  | "CREATED"
  | "LINK_SENT"
  | "CONSENTED"
  | "INFO_CONFIRMED"
  | "INFO_UPDATED"
  | "ONFIDO_PENDING"
  | "ONFIDO_DONE"
  | "DVS_PENDING"
  | "DVS_MATCH"
  | "DVS_NO_MATCH"
  | "DVS_ERROR"
  | "COMPLETE";

export type AdhocKycInvalidReason =
  | "invalid"
  | "expired"
  | "consumed"
  | "revoked"
  | "disabled"
  | "none";

export type AdhocKycStep = "consent" | "details" | "update" | "onfido" | "questions" | "done";

export type AdhocKycDocumentSource = "onfido-complete" | "onfido-partial" | "ask" | "none";

export interface AdhocKycHeldDetails {
  fullName: string;
  middleName?: string;
  dobMasked: string;
  addressMasked: string;
  phoneMasked: string;
  citizenship?: string;
  documentMasked?: string;
}

export interface AdhocKycQuestionOption {
  value: string;
  label: string;
}

/** Hide the question (and lift `required`) when the named single-choice question's answer is one of `values`. */
export interface AdhocKycQuestionSkipRule {
  questionId: string;
  values: string[];
}

export interface AdhocKycQuestion {
  id: string;
  type: "single" | "multi" | "text" | "confirm";
  label: string;
  required: boolean;
  options?: AdhocKycQuestionOption[];
  prefill?: string;
  skipWhen?: AdhocKycQuestionSkipRule;
}

export interface AdhocKycQuestionSchema {
  version: string;
  questions: AdhocKycQuestion[];
}

/** CR3 — a document the customer may choose to be checked against on the update form. */
export interface AdhocKycDocumentOption {
  service: string;
  kind: "driving_licence" | "passport";
  label: string;
  /** DataZoo identifier keys to type for this document (Onfido-held ones excluded). */
  identifierFields: string[];
}

/** Element the document issuer rejected, as the backend reads it from DataZoo's per-source message. */
export type AdhocKycDvsFailedField =
  | "given_names"
  | "family_name"
  | "date_of_birth"
  | "card_number"
  | "document_number";

/** The customer's own typed details from the attempt being corrected (never document identifiers). */
export interface AdhocKycPriorDetails {
  firstName?: string;
  middleName?: string;
  lastName?: string;
  dateOfBirth?: string;
  phone?: string;
  address?: {
    streetAddress?: string;
    city?: string;
    state?: string;
    postcode?: string;
    country?: string;
  };
  primaryCitizenship?: string;
  secondaryCitizenship?: string;
}

/** Details step of a re-opened row: what went wrong last time (in-flow retry or ops re-check). */
export interface AdhocKycPriorAttempt {
  number: number;
  dvsStatus?: "pending" | "match" | "no_match" | "error";
  failedFields: AdhocKycDvsFailedField[];
  reopenedBy?: "customer" | "ops";
  /** The completed biometric check carries over: no second capture, and the document cannot change. */
  reusesIdCheck: boolean;
  details?: AdhocKycPriorDetails;
}

/** Final screen: present iff `POST retry` would be accepted right now. */
export interface AdhocKycRetryOffer {
  failedFields: AdhocKycDvsFailedField[];
  reusesIdCheck: boolean;
}

export interface AdhocKycContextResponse {
  valid: boolean;
  reason?: AdhocKycInvalidReason;
  enabled?: boolean;
  requestId?: string;
  status?: AdhocKycStatus;
  step?: AdhocKycStep;
  firstName?: string;
  residencyCountry?: "AU" | "NZ";
  consentWordingVersion?: string;
  heldDetails?: AdhocKycHeldDetails;
  sufficient?: boolean;
  missingFields?: string[];
  documentSource?: AdhocKycDocumentSource;
  dvsService?: string;
  documentOptions?: AdhocKycDocumentOption[];
  biometricConsentWordingVersion?: string;
  biometricConsentRecorded?: boolean;
  questionSchema?: AdhocKycQuestionSchema;
  consentRecorded?: boolean;
  detailsOutcome?: "confirmed" | "updated";
  questionsSubmitted?: boolean;
  verificationStatus?: "pending" | "match" | "no_match" | "error";
  priorAttempt?: AdhocKycPriorAttempt;
  retry?: AdhocKycRetryOffer;
}

export interface AdhocKycConsentBody {
  wordingVersion: string;
  accepted: true;
}

/** CR2 — same shape as the DVS consent; its own wording version. */
export type AdhocKycBiometricConsentBody = AdhocKycConsentBody;

export interface AdhocKycConfirmDetailsBody {
  attestCurrent: true;
}

export interface AdhocKycAddress {
  streetAddress: string;
  city: string;
  state?: string;
  postcode: string;
  country: "AU" | "NZ";
}

export interface AdhocKycUpdateDetailsBody {
  firstName: string;
  middleName?: string;
  lastName: string;
  dateOfBirth: string;
  phone: string;
  address: AdhocKycAddress;
  primaryCitizenship?: string;
  secondaryCitizenship?: string;
  documentIdentifiers?: Record<string, string>;
  /** CR3 — the chosen document service (one of context.documentOptions[].service). */
  dvsService?: string;
}

export interface AdhocKycOnfidoTokenResponse {
  token: string;
  workflowRunId: string;
}

export interface AdhocKycOnfidoCompleteBody {
  workflowRunId: string;
}

export interface AdhocKycAnswer {
  questionId: string;
  answer: string | string[] | boolean;
}

export interface AdhocKycQuestionsBody {
  schemaVersion: string;
  answers: AdhocKycAnswer[];
}

export interface AdhocKycResendBody {
  token: string;
}

export interface AdhocKycAcceptedResponse {
  accepted: true;
}

/** Header the token-mode client attaches to every `/adhoc-kyc/t/*` call. */
export const ADHOC_KYC_TOKEN_HEADER = "x-adhoc-kyc-token";

/** URL-fragment keys the `/adhoc-kyc/open` bounce writes into `/adhocKyc#…`. */
export const ADHOC_KYC_FRAGMENT_TOKEN = "akt";
export const ADHOC_KYC_FRAGMENT_EXPIRED = "expired";
export const ADHOC_KYC_FRAGMENT_INVALID = "invalid";

// Wave 2 backend additions — error bodies the flow endpoints return (types only).

/**
 * `POST confirm-details` → 400 when the held details are not sufficient for
 * verification. `missingFields` uses the identity field names
 * (`firstName`, `address.city`, …) plus `documentIdentifiers` when the routed
 * DVS service needs identifiers the customer must type; the SPA routes to the
 * update form.
 */
export interface AdhocKycInsufficientDetailsError {
  statusCode: 400;
  message: string;
  missingFields: string[];
}

/** `POST questions` → 400 with one message per problem (unknown id, missing required, option not in list, wrong type). */
export interface AdhocKycInvalidAnswersError {
  statusCode: 400;
  message: "Invalid answers";
  errors: string[];
}

/**
 * `GET onfido-token` answers the SDK token payload for a valid principal, or
 * the usual `{ valid: false, reason }` context shape for a bad token / no open
 * request — never 401.
 */
export type AdhocKycOnfidoTokenResult = AdhocKycOnfidoTokenResponse | AdhocKycContextResponse;

/** Every flow step (`consent`, `confirm-details`, `update-details`, `onfido-complete`, `questions`) answers the refreshed context. */
export type AdhocKycStepResponse = AdhocKycContextResponse;

// ---------------------------------------------------------------------------
// Frontend-only additions (T12). The backend's `openTarget` also writes the
// three bare fragments below; they are not part of the DTO mirror above.
// ---------------------------------------------------------------------------

/** `/adhocKyc#done` — link already consumed; nothing left to do. */
export const ADHOC_KYC_FRAGMENT_DONE = "done";
/** `/adhocKyc#revoked` — ops revoked the request. */
export const ADHOC_KYC_FRAGMENT_REVOKED = "revoked";
/** `/adhocKyc#disabled` — campaign flag is off for this customer. */
export const ADHOC_KYC_FRAGMENT_DISABLED = "disabled";

/** SPA route the `/adhoc-kyc/open` bounce lands on (backend `PORTAL_PATH`). */
export const ADHOC_KYC_PATH = "/adhocKyc";

/** Feature flag the logged-in banner gates on (the server-side gate lives in the guard). */
export const ADHOC_KYC_FLAG = "ADHOC_KYC_AUNZ";

/**
 * Which prefix a call takes. A raw token means the customer arrived from the
 * emailed link (public `/adhoc-kyc/t/*`, token in `x-adhoc-kyc-token`); no
 * token means the logged-in session (`/adhoc-kyc/me/*`, Transmit cookies).
 */
export interface AdhocKycAuth {
  token?: string;
}

/**
 * 4xx body every adhoc POST answers with (400 validation / 409 illegal step).
 * `confirm-details` adds `missingFields`; `questions` adds `errors`.
 */
export interface AdhocKycApiError {
  status?: number;
  message?: string | string[];
  missingFields?: string[];
  errors?: string[];
}

/**
 * Every adhoc call gives up after this long. Without it a context GET that a
 * mobile network drops on the floor (tab backgrounded mid-form, then a
 * `refetchOnFocus`) stays pending forever, and RTK Query dedupes every later
 * refetch of the context onto it — the screen can never move again.
 */
export const ADHOC_KYC_REQUEST_TIMEOUT_MS = 20_000;

/** Builds the url + header pair (and timeout) for a token- or session-mode call. */
export const adhocKycRequest = (
  auth: AdhocKycAuth,
  path: string,
): { url: string; headers?: Record<string, string>; timeout: number } =>
  auth.token
    ? {
        url: `/adhoc-kyc/t/${path}`,
        headers: { [ADHOC_KYC_TOKEN_HEADER]: auth.token },
        timeout: ADHOC_KYC_REQUEST_TIMEOUT_MS,
      }
    : { url: `/adhoc-kyc/me/${path}`, timeout: ADHOC_KYC_REQUEST_TIMEOUT_MS };

/**
 * Human-readable message from an RTK Query error. `axiosBaseQuery` surfaces
 * `{ status, message }` where `message` is the Nest body's `message` — a
 * string, or an array for class-validator failures.
 */
export const adhocKycErrorMessage = (error: unknown): string => {
  const body = (error as AdhocKycApiError | undefined) || {};
  const message = Array.isArray(body.message) ? body.message.join(". ") : body.message;
  const details = [...(body.missingFields || []), ...(body.errors || [])];
  const head = message || "Something went wrong. Please try again.";
  return details.length ? `${head}: ${details.join(", ")}` : head;
};

const ADHOC_KYC_TAG = "AdhocKyc" as const;

/**
 * Every step POST answers with the refreshed context, so write it straight
 * into the `getAdhocKycContext` cache the router renders from. The tag
 * invalidation below still runs, but an invalidation refetch is DROPPED while
 * a context GET is pending (RTK dedupes onto the in-flight request) — and one
 * hung GET then froze the screen on the old step for good: the save had
 * succeeded, the form stayed up, and every further "Save and continue" 409ed
 * (prod 2026-10-30 → 11-04, customers resubmitting up to six times). The
 * router must never depend on a GET to advance.
 */
const applyStepResult = async (
  { auth }: { auth: AdhocKycAuth },
  {
    dispatch,
    queryFulfilled,
  }: {
    dispatch: (action: unknown) => unknown;
    queryFulfilled: Promise<{ data: AdhocKycContextResponse }>;
  },
): Promise<void> => {
  try {
    const { data } = await queryFulfilled;
    if (data && typeof data.valid === "boolean") {
      dispatch(adhocKycApi.util.upsertQueryData("getAdhocKycContext", auth, data));
    }
  } catch {
    // A 4xx keeps the current screen; RTK's own invalidation re-pulls the
    // context where it can (a 409 means the row is already past this step).
  }
};

const adhocKycApi = baseApi.enhanceEndpoints({ addTagTypes: [ADHOC_KYC_TAG] }).injectEndpoints({
  endpoints: (build) => ({
    getAdhocKycContext: build.query<AdhocKycContextResponse, AdhocKycAuth>({
      query: (auth) => ({ ...adhocKycRequest(auth, "context") }),
      providesTags: [ADHOC_KYC_TAG],
    }),
    adhocKycConsent: build.mutation<
      AdhocKycContextResponse,
      { auth: AdhocKycAuth; body: AdhocKycConsentBody }
    >({
      query: ({ auth, body }) => ({ ...adhocKycRequest(auth, "consent"), method: "POST", body }),
      invalidatesTags: [ADHOC_KYC_TAG],
      onQueryStarted: applyStepResult,
    }),
    adhocKycBiometricConsent: build.mutation<
      AdhocKycContextResponse,
      { auth: AdhocKycAuth; body: AdhocKycBiometricConsentBody }
    >({
      query: ({ auth, body }) => ({
        ...adhocKycRequest(auth, "biometric-consent"),
        method: "POST",
        body,
      }),
      invalidatesTags: [ADHOC_KYC_TAG],
      onQueryStarted: applyStepResult,
    }),
    adhocKycConfirmDetails: build.mutation<
      AdhocKycContextResponse,
      { auth: AdhocKycAuth; body: AdhocKycConfirmDetailsBody }
    >({
      query: ({ auth, body }) => ({
        ...adhocKycRequest(auth, "confirm-details"),
        method: "POST",
        body,
      }),
      invalidatesTags: [ADHOC_KYC_TAG],
      onQueryStarted: applyStepResult,
    }),
    // In-flow retry after a DVS no-match: COMPLETE → CONSENTED on the same link; no body.
    adhocKycRetry: build.mutation<AdhocKycContextResponse, { auth: AdhocKycAuth }>({
      query: ({ auth }) => ({ ...adhocKycRequest(auth, "retry"), method: "POST" }),
      invalidatesTags: [ADHOC_KYC_TAG],
      onQueryStarted: applyStepResult,
    }),
    adhocKycUpdateDetails: build.mutation<
      AdhocKycContextResponse,
      { auth: AdhocKycAuth; body: AdhocKycUpdateDetailsBody }
    >({
      query: ({ auth, body }) => ({
        ...adhocKycRequest(auth, "update-details"),
        method: "POST",
        body,
      }),
      invalidatesTags: [ADHOC_KYC_TAG],
      onQueryStarted: applyStepResult,
    }),
    getAdhocKycOnfidoToken: build.query<AdhocKycOnfidoTokenResponse, AdhocKycAuth>({
      query: (auth) => ({ ...adhocKycRequest(auth, "onfido-token") }),
    }),
    adhocKycOnfidoComplete: build.mutation<
      AdhocKycContextResponse,
      { auth: AdhocKycAuth; body: AdhocKycOnfidoCompleteBody }
    >({
      query: ({ auth, body }) => ({
        ...adhocKycRequest(auth, "onfido-complete"),
        method: "POST",
        body,
      }),
      invalidatesTags: [ADHOC_KYC_TAG],
      onQueryStarted: applyStepResult,
    }),
    adhocKycQuestions: build.mutation<
      AdhocKycContextResponse,
      { auth: AdhocKycAuth; body: AdhocKycQuestionsBody }
    >({
      query: ({ auth, body }) => ({ ...adhocKycRequest(auth, "questions"), method: "POST", body }),
      invalidatesTags: [ADHOC_KYC_TAG],
      onQueryStarted: applyStepResult,
    }),
    // Public, no prefix: "send me a fresh link" from the expired page.
    adhocKycResend: build.mutation<AdhocKycAcceptedResponse, AdhocKycResendBody>({
      query: (body) => ({ url: `/adhoc-kyc/resend`, method: "POST", body }),
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetAdhocKycContextQuery,
  useAdhocKycConsentMutation,
  useAdhocKycBiometricConsentMutation,
  useAdhocKycConfirmDetailsMutation,
  useAdhocKycUpdateDetailsMutation,
  useAdhocKycRetryMutation,
  useLazyGetAdhocKycOnfidoTokenQuery,
  useAdhocKycOnfidoCompleteMutation,
  useAdhocKycQuestionsMutation,
  useAdhocKycResendMutation,
} = adhocKycApi;

export { adhocKycApi };
